// Per maand: de drie soorten bedrag per functie, de chat en de lanen (W6 ronde 4).
//
// Wat hier het makkelijkst stilletjes verkeerd gaat (research §3.5 kosten):
//  · vastgelegd, echt en berekend in één kolom of één totaal gooien — berekend
//    is een bovengrens, geen betaald bedrag; daarom drie totalen, elk van de
//    server en elk onder zijn eigen kolom (`totaalVastgelegdUsd`,
//    `totaalEchtUsd`, `totaalBerekendUsd`);
//  · een laan, functie of maand zonder berekend bedrag als $0,00 tonen — de
//    server stuurde null, dus staat er waarom;
//  · een Firecrawl-credit of Exa-verzoek een bedrag geven — die hebben geen
//    prijs zolang de owner het abonnement niet invulde; ze blijven een aantal.
//
// Lanen staan onder hun functie, met het woord dat de server voor die functie
// stuurt; elke laan zelf met zijn `laanLabel` (dezelfde lanenlijst als
// Gezondheid), nooit met de sleutel. Heeft een laan geen berekend bedrag, dan
// staat de zin van de server er (`reden`) — dit scherm bedenkt geen eigen reden.

import { Fragment, useMemo, useState } from "react";

import { Card, ReasonBadge } from "../../components/ui";
import { EmptyReason } from "../../components/Empty";
import type { KostenLaan, KostenMaand, KostenResponse } from "../../contracts";
import { color, radius, size, weight } from "../../tokens";
import { Blok, aantal, cijfer, getal, noot } from "./BronnenStukken";
import { Bedrag, Tabel, cel, klein, kopCel, maandNaam, usd } from "./KostenStukken";
import { PRIJSLOOS_LABEL, type Prijsloos } from "./woorden";

type ZonderPrijs = KostenResponse["prijzen"]["zonderPrijs"];

export function PerMaand({
  maanden,
  uitleg,
  zonderPrijs,
}: {
  maanden: readonly KostenMaand[];
  /** Wat "een maand" hier betekent — de zin van de server (`maandUitleg`). */
  uitleg: string;
  zonderPrijs: ZonderPrijs;
}) {
  // Nieuwste eerst: wie dit vlak opent, wil weten hoe het er NU voor staat.
  const volgorde = useMemo(
    () => [...maanden].sort((a, b) => b.maand.localeCompare(a.maand)),
    [maanden],
  );
  const [gekozen, setGekozen] = useState<string | null>(null);
  const maand = volgorde.find((m) => m.maand === gekozen) ?? volgorde[0];

  if (!maand) {
    return (
      <Blok titel="Per maand">
        <Card style={{ padding: 18 }}>
          <EmptyReason reason="er is nog geen run of chatvraag met kosten geteld" />
        </Card>
      </Blok>
    );
  }

  return (
    <Blok
      titel="Per maand"
      rechts={<MaandKeuze maanden={volgorde} actief={maand.maand} onKies={setGekozen} />}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p data-maanduitleg style={{ ...noot, marginTop: 0 }}>
          {uitleg}
        </p>
        <FunctieTabel maand={maand} />
        <Chat gesprekken={maand.gesprekken} />
        <p style={{ ...noot, marginTop: 0 }}>
          Drie soorten bedrag, elk in een eigen kolom en met een eigen totaal. Vastgelegd: wat
          een run zelf bewaarde. Echt: wat OpenRouter per chatvraag meestuurde. Berekend: tokens
          maal de prijs van het model dat de laan nu gebruikt — een bovengrens.
        </p>
        <h3 style={{ fontSize: size.meta, fontWeight: weight.medium, color: color.ink, marginTop: 6 }}>
          Per laan
        </h3>
        <LaanTabel maand={maand} zonderPrijs={zonderPrijs} />
      </div>
    </Blok>
  );
}

/** Zelfde gesegmenteerde knoppen als de vlakkeuze van Beheer: één patroon voor
 *  "kies wat je ziet", en een lijst in plaats van een uitklapmenu. */
function MaandKeuze({
  maanden,
  actief,
  onKies,
}: {
  maanden: readonly KostenMaand[];
  actief: string;
  onKies: (maand: string) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Kies een maand"
      style={{
        display: "inline-flex",
        flexWrap: "wrap",
        gap: 4,
        background: color.lineFaint,
        borderRadius: radius.input,
        padding: 3,
      }}
    >
      {maanden.map((m) => (
        <button
          key={m.maand}
          type="button"
          className={"view-btn" + (m.maand === actief ? " active" : "")}
          aria-pressed={m.maand === actief}
          onClick={() => onKies(m.maand)}
        >
          {maandNaam(m.maand)}
        </button>
      ))}
    </div>
  );
}

function FunctieTabel({ maand }: { maand: KostenMaand }) {
  return (
    <Tabel label={`Kosten per functie, ${maandNaam(maand.maand)}`} minBreedte={620}>
      <thead>
        <tr>
          <th style={kopCel()}>Functie</th>
          <th style={kopCel(true)}>Vastgelegd</th>
          <th style={kopCel(true)}>Echt (chat)</th>
          <th style={kopCel(true)}>Berekend (bovengrens)</th>
        </tr>
      </thead>
      <tbody>
        {maand.functies.length === 0 ? (
          <tr>
            <td colSpan={4} style={cel()}>
              <EmptyReason reason="de server gaf voor deze maand geen functies" />
            </td>
          </tr>
        ) : (
          maand.functies.map((f) => (
            <tr key={f.functie} data-functie={f.functie}>
              <td style={{ ...cel(), color: color.ink }}>{f.label}</td>
              <td style={cel(true)}>
                <Bedrag n={f.vastgelegdUsd} />
              </td>
              <td style={cel(true)}>
                <Bedrag n={f.echtUsd} />
              </td>
              <td style={cel(true)}>
                {/* null = geen laan van deze functie had een prijs: de reden,
                    nooit een verzonnen $0,00. */}
                {f.berekendUsd !== null ? (
                  <Bedrag n={f.berekendUsd} />
                ) : (
                  <EmptyReason reason={f.reden ?? "de server noemt geen reden"} />
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
      <tfoot>
        {/* Drie totalen van de server, elk onder zijn eigen kolom en nooit bij
            elkaar opgeteld: vastgelegd is bewaard, echt is betaald, berekend is
            een bovengrens. */}
        <tr data-rij="totaal">
          <td style={{ ...cel(), ...totaalRand, fontWeight: weight.medium, color: color.ink }}>
            Totaal
          </td>
          <td style={{ ...cel(true), ...totaalRand }}>
            <TotaalBedrag n={maand.totaalVastgelegdUsd} wat="vastgelegd" />
          </td>
          <td style={{ ...cel(true), ...totaalRand }}>
            <TotaalBedrag n={maand.totaalEchtUsd} wat="echt" />
          </td>
          <td style={{ ...cel(true), ...totaalRand }}>
            {maand.totaalBerekendUsd !== null ? (
              <TotaalBedrag n={maand.totaalBerekendUsd} wat="berekend, bovengrens" />
            ) : (
              <EmptyReason reason="deze maand viel er niets te berekenen" />
            )}
          </td>
        </tr>
      </tfoot>
    </Tabel>
  );
}

const totaalRand = { borderTop: `1px solid ${color.border}` };

function TotaalBedrag({ n, wat }: { n: number; wat: string }) {
  return (
    <>
      <div style={{ fontWeight: weight.bold, color: color.ink }}>{usd(n)}</div>
      <div style={klein}>{wat}</div>
    </>
  );
}

function Chat({ gesprekken: g }: { gesprekken: KostenMaand["gesprekken"] }) {
  return (
    <Card style={{ padding: "12px 16px" }}>
      <div
        data-chat
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "baseline",
          fontSize: size.meta,
          color: color.body,
        }}
      >
        <span style={{ fontWeight: weight.medium, color: color.ink }}>Chatvragen</span>
        {g === null ? (
          <EmptyReason reason="in deze maand is geen chatvraag geteld" />
        ) : (
          <>
            <span style={cijfer}>{aantal(g.aantal, "vraag", "vragen")}</span>
            <span style={{ color: color.faint, ...cijfer }}>{`· ${getal(g.metBedrag)} met bedrag ·`}</span>
            <span title="Alleen de vragen waarbij OpenRouter een bedrag meestuurde.">
              {"echt "}
              <span style={{ fontWeight: weight.bold, color: color.ink, ...cijfer }}>
                {usd(g.echtUsd)}
              </span>
            </span>
          </>
        )}
      </div>
    </Card>
  );
}

const LAAN_KOPPEN = [
  { label: "Laan", rechts: false },
  { label: "Runs", rechts: true },
  { label: "Model", rechts: false },
  { label: "Tokens in · uit", rechts: true },
  // Het woord uit dezelfde lijst als de prijzenuitleg (woorden.ts).
  { label: `${PRIJSLOOS_LABEL.firecrawl}-credits`, rechts: true },
  { label: `${PRIJSLOOS_LABEL.exa}-verzoeken`, rechts: true },
  { label: "Vastgelegd", rechts: true },
  { label: "Berekend (bovengrens)", rechts: true },
] as const;

function LaanTabel({ maand, zonderPrijs }: { maand: KostenMaand; zonderPrijs: ZonderPrijs }) {
  if (maand.lanen.length === 0) {
    return (
      <Card style={{ padding: 18 }}>
        <EmptyReason reason="in deze maand is geen run geladen" />
      </Card>
    );
  }
  // Opzoeken, geen rekensom: welke dingen de server zonder prijs meldt.
  const redenZonderPrijs = new Map(zonderPrijs.map((z) => [z.wat, z.reden]));

  return (
    <Tabel label={`Kosten per laan, ${maandNaam(maand.maand)}`} minBreedte={960}>
      <thead>
        <tr>
          {LAAN_KOPPEN.map((k) => (
            <th key={k.label} style={kopCel(k.rechts)}>
              {k.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {groepeer(maand).map((g) => (
          <Fragment key={g.functie}>
            <tr data-groep={g.functie}>
              <td colSpan={LAAN_KOPPEN.length} style={groepCel}>
                {g.label}
              </td>
            </tr>
            {g.lanen.map((l) => (
              <LaanRij key={l.laan} laan={l} redenZonderPrijs={redenZonderPrijs} />
            ))}
          </Fragment>
        ))}
      </tbody>
    </Tabel>
  );
}

const groepCel = {
  padding: "8px 14px",
  fontSize: 11,
  fontWeight: 600,
  color: color.soft,
  background: color.surfaceAlt,
  borderTop: `1px solid ${color.lineFaint}`,
} as const;

/** De lanen onder hun functie, in de volgorde van de functietabel erboven. Een
 *  functie die de server niet in `functies` zette (hoort niet voor te komen)
 *  krijgt zijn sleutel als kop — kaal maar eerlijk. */
function groepeer(maand: KostenMaand): { functie: string; label: string; lanen: KostenLaan[] }[] {
  const label = new Map(maand.functies.map((f) => [f.functie, f.label]));
  const per = new Map<string, KostenLaan[]>();
  for (const l of maand.lanen) per.set(l.functie, [...(per.get(l.functie) ?? []), l]);
  const bekend = maand.functies.map((f) => f.functie).filter((f) => per.has(f));
  const los = [...per.keys()].filter((f) => !label.has(f));
  return [...bekend, ...los].map((f) => ({ functie: f, label: label.get(f) ?? f, lanen: per.get(f)! }));
}

function LaanRij({
  laan: l,
  redenZonderPrijs,
}: {
  laan: KostenLaan;
  redenZonderPrijs: ReadonlyMap<string, string | null>;
}) {
  return (
    <tr data-laan={l.laan}>
      <td style={{ ...cel(), color: color.ink, fontWeight: weight.medium }}>{l.laanLabel}</td>
      <td style={cel(true)}>
        {getal(l.runs)}
        <div style={klein}>{`${getal(l.runsMetBedrag)} met bedrag`}</div>
      </td>
      {/* null zegt alleen dat de server geen model kent: "geen taalmodel" of
          "nog niet in de prijstabel" staat in zijn reden, niet hier. */}
      <td style={cel()}>{l.model ?? <EmptyReason reason="geen model bekend" />}</td>
      <td style={cel(true)}>
        {l.tokensIn === 0 && l.tokensUit === 0 ? (
          <EmptyReason reason="geen tokens geteld" />
        ) : (
          `${getal(l.tokensIn)} · ${getal(l.tokensUit)}`
        )}
      </td>
      {/* De kolommen zijn die van het contract (firecrawlCredits, exaVerzoeken);
          welke sleutel van `prijzen.zonderPrijs` erbij hoort, controleert de
          typecheck tegen PRIJSLOOS_LABEL. Of er een "geen prijs" staat, zegt
          de server: alleen wat hij zonder prijs meldt, krijgt het label. */}
      <td style={cel(true)}>
        <AantalZonderPrijs n={l.firecrawlCredits} reden={redenZonderPrijs} wat="firecrawl" />
      </td>
      <td style={cel(true)}>
        <AantalZonderPrijs n={l.exaVerzoeken} reden={redenZonderPrijs} wat="exa" />
      </td>
      {/* Vastgelegd is altijd een getal van de server; 0 betekent dat geen run
          een bedrag bewaarde, en staat er dus gewoon (licht) als $0,00. */}
      <td style={cel(true)}>
        <Bedrag n={l.vastgelegdUsd} />
      </td>
      <td style={cel(true)}>
        {l.berekendUsd !== null ? (
          <Bedrag n={l.berekendUsd} />
        ) : (
          <EmptyReason reason={l.reden ?? "de server noemt geen reden"} />
        )}
      </td>
    </tr>
  );
}

/** Een aantal credits of verzoeken. Heeft het geen prijs, dan zegt een label
 *  dat, met de reden van de server in de tooltip — nooit een bedrag. */
function AantalZonderPrijs({
  n,
  wat,
  reden,
}: {
  n: number;
  wat: Prijsloos;
  /** `prijzen.zonderPrijs` van de server, op zijn sleutel (`wat`). */
  reden: ReadonlyMap<string, string | null>;
}) {
  if (n === 0) return <span style={{ color: color.faint }}>0</span>;
  return (
    <>
      <span style={{ color: color.ink }}>{getal(n)}</span>
      {reden.has(wat) && (
        <div style={{ marginTop: 3 }}>
          <ReasonBadge text="geen prijs" title={reden.get(wat) ?? "de server noemt geen reden"} />
        </div>
      )}
    </>
  );
}
